import { useSyncExternalStore } from "react";
import { request, RequestError } from "../lib/api";
import { getAssignedID, getToken } from "../lib/auth";
import * as idb from "./idb";

// Durable queue for everything a teacher writes while working through a
// module — progress, quiz answers, form fields, reflections, activity
// responses. Online, writes go straight to the API as before; once a write
// fails for lack of a connection (or anything is already waiting) it joins
// this queue, in order, and is replayed when the connection returns.
//
// Quiz attempts and form submissions are created server-side and their ids
// appear in later paths (…/attempts/{id}/responses/…). Offline, the caller
// gets a local placeholder ("local:<uuid>") instead; the queue records the
// real id when the create call finally succeeds and rewrites later paths.
//
// Storage is IndexedDB (see idb.ts). An in-memory mirror answers the
// synchronous questions (is anything queued? which items are pending?) and
// every IndexedDB write goes through one promise chain, so the stored order
// always matches the order writes were made.
const MAX_SERVER_ERRORS = 5;
const RETRY_INTERVAL_MS = 15_000;

// Where the queue lived before it moved to IndexedDB; migrated on load.
const LEGACY_OUTBOX_KEY = "cerp-teacher-outbox";
const LEGACY_REFS_KEY = "cerp-teacher-outbox-refs";

export interface QueuedWrite {
  /** IndexedDB key, assigned on first save; also the replay order. */
  seq?: number;
  id: string;
  owner: string;
  method: "POST" | "PUT";
  path: string;
  body?: unknown;
  /** Local placeholder id this write creates (quiz attempt, form submission). */
  createsRef?: string;
  queuedAt: number;
  serverErrors: number;
}

export interface WriteQueueState {
  pending: number;
  /** Writes the server refused on replay (e.g. attempt limit) — dropped. */
  rejected: number;
  syncing: boolean;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let state: WriteQueueState = { pending: 0, rejected: 0, syncing: false };
let entries: QueuedWrite[] = [];
let refs = new Map<string, string>();
let loaded: Promise<void> | null = null;
let persisting: Promise<unknown> = Promise.resolve();
let flushing: Promise<void> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function setState(patch: Partial<WriteQueueState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

// Chains one IndexedDB operation behind all earlier ones. A failure (quota,
// private mode) is swallowed: the in-memory copy still carries the write
// for as long as the app stays open.
function persist(operation: () => Promise<unknown>): Promise<unknown> {
  persisting = persisting.then(operation).catch(() => undefined);
  return persisting;
}

function entriesChanged(): void {
  setState({ pending: entries.length });
  if (entries.length && !timer) timer = setInterval(() => void flushWrites(), RETRY_INTERVAL_MS);
  if (!entries.length && timer) {
    clearInterval(timer);
    timer = null;
  }
}

function readLegacy<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(localStorage.getItem(key) || "null") ?? fallback;
  } catch {
    return fallback;
  }
}

async function load(): Promise<void> {
  try {
    const stored = await idb.getAll<QueuedWrite>(idb.OUTBOX_STORE);
    const storedRefs = await idb.getAllEntries<string>(idb.REFS_STORE);
    const legacy = readLegacy<QueuedWrite[]>(LEGACY_OUTBOX_KEY, []);
    const legacyRefs = readLegacy<Record<string, string>>(LEGACY_REFS_KEY, {});
    for (const entry of legacy) {
      stored.push({ ...entry, seq: Number(await idb.add(idb.OUTBOX_STORE, entry)) });
    }
    for (const [local, real] of Object.entries(legacyRefs)) {
      await idb.put(idb.REFS_STORE, real, local);
      storedRefs.push([local, real]);
    }
    localStorage.removeItem(LEGACY_OUTBOX_KEY);
    localStorage.removeItem(LEGACY_REFS_KEY);
    // Anything queued before the load finished comes after what was stored.
    entries = [...stored.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0)), ...entries];
    refs = new Map([...storedRefs, ...refs]);
  } catch {
    // IndexedDB unavailable: run on the in-memory queue alone.
  }
  entriesChanged();
}

function ensureLoaded(): Promise<void> {
  if (!loaded) loaded = load();
  return loaded;
}

function saveRef(local: string, real: string): void {
  refs.set(local, real);
  void persist(() => idb.put(idb.REFS_STORE, real, local));
}

function enqueue(write: Omit<QueuedWrite, "id" | "owner" | "queuedAt" | "serverErrors">): void {
  const entry: QueuedWrite = {
    ...write,
    id: crypto.randomUUID(),
    owner: getAssignedID(),
    queuedAt: Date.now(),
    serverErrors: 0,
  };
  entries = [...entries, entry];
  entriesChanged();
  void persist(async () => {
    entry.seq = Number(await idb.add(idb.OUTBOX_STORE, entry));
  });
}

function removeEntry(entry: QueuedWrite): void {
  entries = entries.filter((candidate) => candidate.id !== entry.id);
  entriesChanged();
  // Runs after the add above in the same chain, so seq is known by now.
  void persist(() =>
    entry.seq === undefined ? Promise.resolve() : idb.remove(idb.OUTBOX_STORE, entry.seq)
  );
}

export function isLocalRef(id: string): boolean {
  return id.startsWith("local:");
}

const LOCAL_REF = /local:[0-9a-f-]+/g;

// Swaps every placeholder in a path for its real id; null while any
// placeholder is still unresolved (its create call hasn't gone through).
function resolvePath(path: string): string | null {
  let unresolved = false;
  const resolved = path.replace(LOCAL_REF, (ref) => {
    const real = refs.get(ref);
    if (real) return real;
    unresolved = true;
    return ref;
  });
  return unresolved ? null : resolved;
}

function isNetworkError(cause: unknown): boolean {
  return !(cause instanceof RequestError);
}

export type WriteResult<T> = { queued: false; data: T } | { queued: true };

// sendWrite is the one path every teacher write goes through. It resolves
// with { queued: true } when the write was saved for later; it only rejects
// when the server itself refused the write, which callers surface as before.
export async function sendWrite<T = unknown>(write: {
  method: "POST" | "PUT";
  path: string;
  body?: unknown;
  createsRef?: string;
}): Promise<WriteResult<T>> {
  await ensureLoaded();
  const path = resolvePath(write.path);
  // Order matters (an answer must not reach the server before its attempt
  // exists), so once anything is queued, everything after it queues too.
  if (path === null || entries.length > 0 || !navigator.onLine) {
    enqueue(write);
    void flushWrites();
    return { queued: true };
  }
  try {
    const data = await request<T>(path, {
      method: write.method,
      body: write.body === undefined ? undefined : JSON.stringify(write.body),
    });
    if (write.createsRef) saveRef(write.createsRef, (data as { id: string }).id);
    return { queued: false, data };
  } catch (cause) {
    if (!isNetworkError(cause)) throw cause;
    enqueue(write);
    return { queued: true };
  }
}

// A flush asked for while one is running (say, "online" fires just as a
// drain gives up for being offline) runs again once that one ends, rather
// than being folded into a run that has already decided to stop.
let flushAgain = false;

export function flushWrites(): Promise<void> {
  if (flushing) {
    flushAgain = true;
    return flushing;
  }
  flushing = (async () => {
    do {
      flushAgain = false;
      await drain();
    } while (flushAgain);
  })().finally(() => {
    flushing = null;
    setState({ syncing: false });
  });
  return flushing;
}

async function drain(): Promise<void> {
  await ensureLoaded();
  if (!getToken() || !navigator.onLine) return;
  setState({ syncing: true });
  for (;;) {
    const next = entries.find((entry) => entry.owner === getAssignedID());
    if (!next) break;
    const path = resolvePath(next.path);
    // A placeholder that can never resolve means its create call was
    // refused and dropped — nothing that depends on it can succeed either.
    if (path === null) {
      removeEntry(next);
      setState({ rejected: state.rejected + 1 });
      continue;
    }
    try {
      const data = await request<{ id?: string }>(path, {
        method: next.method,
        body: next.body === undefined ? undefined : JSON.stringify(next.body),
      });
      if (next.createsRef && data?.id) saveRef(next.createsRef, data.id);
      removeEntry(next);
    } catch (cause) {
      if (isNetworkError(cause)) break;
      const status = (cause as RequestError).status;
      if (status === 401) break;
      if (status >= 500 && next.serverErrors + 1 < MAX_SERVER_ERRORS) {
        next.serverErrors += 1;
        void persist(() =>
          next.seq === undefined ? Promise.resolve() : idb.put(idb.OUTBOX_STORE, next)
        );
        break;
      }
      removeEntry(next);
      setState({ rejected: state.rejected + 1 });
    }
  }
  await persisting;
}

// Item ids whose completion is still sitting in the queue. The server's
// copy of the module doesn't know about them yet, so the player overlays
// them — otherwise a teacher would find finished items "not started" again
// and block_next would re-lock the next one.
export async function pendingCompletedItemIDs(): Promise<Set<string>> {
  await ensureLoaded();
  const ids = new Set<string>();
  for (const entry of entries) {
    const progress = entry.path.match(/^\/api\/teacher\/progress\/([^/]+)$/);
    if (progress && (entry.body as { status?: string } | undefined)?.status === "completed") {
      ids.add(progress[1]);
    }
    const response = entry.path.match(
      /^\/api\/teacher\/(?:reflections|activities)\/([^/]+)\/responses$/
    );
    if (response) ids.add(response[1]);
  }
  return ids;
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useWriteQueue(): WriteQueueState {
  return useSyncExternalStore(subscribe, () => state);
}

export function initWriteQueue(): void {
  window.addEventListener("online", () => void flushWrites());
  void flushWrites();
}

// Resolves once every queued IndexedDB write has landed.
export function whenPersisted(): Promise<unknown> {
  return persisting;
}

// Tests only: forget in-memory state so each test starts from IndexedDB.
export function resetWriteQueueForTests(): void {
  entries = [];
  refs = new Map();
  loaded = null;
  persisting = Promise.resolve();
  flushing = null;
  flushAgain = false;
  if (timer) clearInterval(timer);
  timer = null;
  state = { pending: 0, rejected: 0, syncing: false };
}
